import moment from 'moment-timezone';

/**
 * دوال مساعدة للتعامل مع توقيت الفرع في جميع أجزاء النظام
 * Utility functions for handling branch timezone across the system
 */

/**
 * الحصول على الوقت الحالي بتوقيت الفرع
 * Get current time in branch timezone
 * 
 * @param timezone - توقيت الفرع (مثال: 'Asia/Riyadh')
 * @returns كائن moment بتوقيت الفرع
 */
export const getCurrentBranchTime = (timezone: string = 'Asia/Riyadh'): moment.Moment => {
  return moment().tz(timezone);
};

/**
 * تحويل تاريخ معين إلى توقيت الفرع
 * Convert a date to branch timezone
 * 
 * @param date - التاريخ المراد تحويله
 * @param timezone - توقيت الفرع
 * @returns كائن moment بتوقيت الفرع
 */
export const convertToBranchTimezone = (
  date: string | Date | moment.Moment,
  timezone: string = 'Asia/Riyadh'
): moment.Moment => {
  return moment(date).tz(timezone);
};

/**
 * تنسيق تاريخ بتوقيت الفرع
 * Format a date in branch timezone
 * 
 * @param date - التاريخ المراد تنسيقه
 * @param timezone - توقيت الفرع
 * @param format - صيغة التنسيق (افتراضي: 'YYYY-MM-DD HH:mm:ss')
 * @returns التاريخ المنسق كنص
 */
export const formatDateInBranchTimezone = (
  date: string | Date | moment.Moment,
  timezone: string = 'Asia/Riyadh',
  format: string = 'YYYY-MM-DD HH:mm:ss'
): string => {
  if (!date) return '';
  return moment(date).tz(timezone).format(format);
};

/**
 * تنسيق التاريخ فقط (بدون الوقت) بتوقيت الفرع
 * Format date only (without time) in branch timezone
 * 
 * @param date - التاريخ المراد تنسيقه
 * @param timezone - توقيت الفرع
 * @returns التاريخ المنسق
 */
export const formatDateOnly = (
  date: string | Date | moment.Moment,
  timezone: string = 'Asia/Riyadh'
): string => {
  if (!date) return '';
  return moment(date).tz(timezone).format('YYYY-MM-DD');
};

/**
 * تنسيق الوقت فقط (بدون التاريخ) بتوقيت الفرع
 * Format time only (without date) in branch timezone
 * 
 * @param date - التاريخ المراد تنسيقه
 * @param timezone - توقيت الفرع
 * @returns الوقت المنسق
 */
export const formatTimeOnly = (
  date: string | Date | moment.Moment,
  timezone: string = 'Asia/Riyadh'
): string => {
  if (!date) return '';
  return moment(date).tz(timezone).format('HH:mm:ss');
};

/**
 * تنسيق التاريخ والوقت بصيغة عربية
 * Format date and time in Arabic format
 * 
 * @param date - التاريخ المراد تنسيقه
 * @param timezone - توقيت الفرع
 * @returns التاريخ المنسق بالعربية
 */
export const formatDateTimeArabic = (
  date: string | Date | moment.Moment,
  timezone: string = 'Asia/Riyadh'
): string => {
  if (!date) return '';
  return moment(date).tz(timezone).locale('ar').format('DD/MM/YYYY - hh:mm A');
};

/**
 * الحصول على بداية اليوم بتوقيت الفرع
 * Get start of day in branch timezone
 * 
 * @param date - التاريخ (افتراضي: اليوم)
 * @param timezone - توقيت الفرع
 * @returns بداية اليوم بتوقيت الفرع
 */
export const getStartOfDay = (
  date: string | Date | moment.Moment = new Date(),
  timezone: string = 'Asia/Riyadh'
): moment.Moment => {
  return moment(date).tz(timezone).startOf('day');
};

/**
 * الحصول على نهاية اليوم بتوقيت الفرع
 * Get end of day in branch timezone
 * 
 * @param date - التاريخ (افتراضي: اليوم)
 * @param timezone - توقيت الفرع
 * @returns نهاية اليوم بتوقيت الفرع
 */
export const getEndOfDay = (
  date: string | Date | moment.Moment = new Date(),
  timezone: string = 'Asia/Riyadh'
): moment.Moment => {
  return moment(date).tz(timezone).endOf('day');
};

/**
 * الحصول على بداية الشهر بتوقيت الفرع
 * Get start of month in branch timezone
 * 
 * @param date - التاريخ (افتراضي: الشهر الحالي)
 * @param timezone - توقيت الفرع
 * @returns بداية الشهر بتوقيت الفرع
 */
export const getStartOfMonth = (
  date: string | Date | moment.Moment = new Date(),
  timezone: string = 'Asia/Riyadh'
): moment.Moment => {
  return moment(date).tz(timezone).startOf('month');
};

/**
 * الحصول على نهاية الشهر بتوقيت الفرع
 * Get end of month in branch timezone
 * 
 * @param date - التاريخ (افتراضي: الشهر الحالي)
 * @param timezone - توقيت الفرع
 * @returns نهاية الشهر بتوقيت الفرع
 */
export const getEndOfMonth = (
  date: string | Date | moment.Moment = new Date(),
  timezone: string = 'Asia/Riyadh'
): moment.Moment => {
  return moment(date).tz(timezone).endOf('month');
};

/**
 * تحويل تاريخ من توقيت UTC إلى توقيت الفرع
 * Convert UTC date to branch timezone
 * 
 * @param utcDate - التاريخ بتوقيت UTC
 * @param timezone - توقيت الفرع
 * @returns التاريخ بتوقيت الفرع
 */
export const convertFromUTC = (
  utcDate: string | Date,
  timezone: string = 'Asia/Riyadh'
): moment.Moment => {
  return moment.utc(utcDate).tz(timezone);
};

/**
 * تحويل تاريخ من توقيت الفرع إلى UTC
 * Convert branch timezone date to UTC
 * 
 * @param date - التاريخ بتوقيت الفرع
 * @param timezone - توقيت الفرع
 * @returns التاريخ بتوقيت UTC
 */
export const convertToUTC = (
  date: string | Date | moment.Moment,
  timezone: string = 'Asia/Riyadh'
): moment.Moment => {
  return moment.tz(date, timezone).utc();
};

/**
 * التحقق من صحة المنطقة الزمنية
 * Validate timezone
 * 
 * @param timezone - المنطقة الزمنية المراد التحقق منها
 * @returns true إذا كانت صحيحة
 */
export const isValidTimezone = (timezone: string): boolean => {
  return moment.tz.zone(timezone) !== null;
};

/**
 * الحصول على قائمة بجميع المناطق الزمنية المتاحة
 * Get list of all available timezones
 * 
 * @returns قائمة المناطق الزمنية
 */
export const getAllTimezones = (): string[] => {
  return moment.tz.names();
};

/**
 * حساب الفرق بين تاريخين بتوقيت الفرع
 * Calculate difference between two dates in branch timezone
 * 
 * @param date1 - التاريخ الأول
 * @param date2 - التاريخ الثاني
 * @param unit - وحدة القياس (days, hours, minutes, etc.)
 * @param timezone - توقيت الفرع
 * @returns الفرق بين التاريخين
 */
export const getDateDifference = (
  date1: string | Date | moment.Moment,
  date2: string | Date | moment.Moment,
  unit: moment.unitOfTime.Diff = 'days',
  timezone: string = 'Asia/Riyadh'
): number => {
  const d1 = moment(date1).tz(timezone);
  const d2 = moment(date2).tz(timezone);
  return d1.diff(d2, unit);
};

/**
 * إضافة مدة زمنية لتاريخ معين بتوقيت الفرع
 * Add duration to a date in branch timezone
 * 
 * @param date - التاريخ الأساسي
 * @param amount - المقدار المراد إضافته
 * @param unit - وحدة القياس (days, hours, minutes, etc.)
 * @param timezone - توقيت الفرع
 * @returns التاريخ الجديد
 */
export const addDuration = (
  date: string | Date | moment.Moment,
  amount: number,
  unit: moment.unitOfTime.DurationConstructor = 'days',
  timezone: string = 'Asia/Riyadh'
): moment.Moment => {
  return moment(date).tz(timezone).add(amount, unit);
};

/**
 * طرح مدة زمنية من تاريخ معين بتوقيت الفرع
 * Subtract duration from a date in branch timezone
 * 
 * @param date - التاريخ الأساسي
 * @param amount - المقدار المراد طرحه
 * @param unit - وحدة القياس (days, hours, minutes, etc.)
 * @param timezone - توقيت الفرع
 * @returns التاريخ الجديد
 */
export const subtractDuration = (
  date: string | Date | moment.Moment,
  amount: number,
  unit: moment.unitOfTime.DurationConstructor = 'days',
  timezone: string = 'Asia/Riyadh'
): moment.Moment => {
  return moment(date).tz(timezone).subtract(amount, unit);
};

/**
 * التحقق من أن تاريخ معين هو اليوم بتوقيت الفرع
 * Check if a date is today in branch timezone
 * 
 * @param date - التاريخ المراد التحقق منه
 * @param timezone - توقيت الفرع
 * @returns true إذا كان اليوم
 */
export const isToday = (
  date: string | Date | moment.Moment,
  timezone: string = 'Asia/Riyadh'
): boolean => {
  const today = moment().tz(timezone).startOf('day');
  const checkDate = moment(date).tz(timezone).startOf('day');
  return today.isSame(checkDate);
};

/**
 * التحقق من أن تاريخ معين في الماضي بتوقيت الفرع
 * Check if a date is in the past in branch timezone
 * 
 * @param date - التاريخ المراد التحقق منه
 * @param timezone - توقيت الفرع
 * @returns true إذا كان في الماضي
 */
export const isPast = (
  date: string | Date | moment.Moment,
  timezone: string = 'Asia/Riyadh'
): boolean => {
  const now = moment().tz(timezone);
  const checkDate = moment(date).tz(timezone);
  return checkDate.isBefore(now);
};

/**
 * التحقق من أن تاريخ معين في المستقبل بتوقيت الفرع
 * Check if a date is in the future in branch timezone
 * 
 * @param date - التاريخ المراد التحقق منه
 * @param timezone - توقيت الفرع
 * @returns true إذا كان في المستقبل
 */
export const isFuture = (
  date: string | Date | moment.Moment,
  timezone: string = 'Asia/Riyadh'
): boolean => {
  const now = moment().tz(timezone);
  const checkDate = moment(date).tz(timezone);
  return checkDate.isAfter(now);
};
